/**
 * Recently Purchased Notifications
 * Sleek social proof notifications with elegant GSAP animations.
 */

(function() {
  'use strict';

  const config = window.recentlyPurchasedConfig;
  if (!config || !config.enabled) return;

  const DISMISSED_KEY = 'rp_notifications_dismissed';

  let products = [];
  let isRunning = false;
  let timeoutId = null;
  let productsFetched = false;
  let container = null;
  let currentNotification = null;

  function isDismissedForSession() {
    try { return sessionStorage.getItem(DISMISSED_KEY) === 'true'; } 
    catch { return false; }
  }

  function dismissForSession() {
    try { sessionStorage.setItem(DISMISSED_KEY, 'true'); } 
    catch {}
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function hasGSAP() {
    return typeof gsap !== 'undefined';
  }

  const requestIdle = window.requestIdleCallback || function(cb) {
    return setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 50 }), 1);
  };

  function randomItem(arr) {
    if (!arr || arr.length === 0) return '';
    const item = arr[Math.floor(Math.random() * arr.length)];
    return typeof item === 'string' ? item.trim() : item;
  }

  function randomProduct(arr) {
    if (!arr || arr.length === 0) return null;
    return arr[Math.floor(Math.random() * arr.length)];
  }

  function randomBetween(min, max) {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  }

  function formatTimeAgo(minutes) {
    if (minutes === 1) return '1 min ago';
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    return hours === 1 ? '1 hr ago' : `${hours} hrs ago`;
  }

  function getContainer() {
    if (container) return container;
    container = document.createElement('div');
    container.className = `rp-notification-container rp-notification-container--${config.position}`;
    container.setAttribute('role', 'region');
    container.setAttribute('aria-label', 'Purchase notifications');
    document.body.appendChild(container);
    return container;
  }

  function initMarquee(wrapper) {
    if (!wrapper) return;
    const title = wrapper.querySelector('.rp-notification__title');
    if (!title) return;
    
    // Check if text overflows
    const wrapperWidth = wrapper.offsetWidth;
    const textWidth = title.scrollWidth / 2; // Divide by 2 because we have duplicate
    
    if (textWidth > wrapperWidth) {
      wrapper.classList.add('is-marquee');
    }
  }

  function createNotificationElement(product, customerInfo, timeAgo) {
    const el = document.createElement('div');
    el.className = 'rp-notification';
    el.setAttribute('role', 'alert');
    el.setAttribute('data-product-url', product.handle ? `/products/${product.handle}` : '#');
    
    // Apply border radius from settings
    const radius = config.borderRadius || 8;
    el.style.setProperty('--rp-radius', `${radius}px`);

    const imageUrl = config.showImage && product.images?.length > 0
      ? product.images[0].src
      : null;

    el.innerHTML = `
      ${imageUrl ? `
        <div class="rp-notification__image">
          <img src="${imageUrl}" alt="${product.title}" loading="lazy">
        </div>
      ` : ''}
      <div class="rp-notification__content">
        <div class="rp-notification__title-wrapper">
          <span class="rp-notification__title">
            <span class="rp-notification__title-text">${product.title}</span>
            <span class="rp-notification__title-text" aria-hidden="true">${product.title}</span>
          </span>
        </div>
        <p class="rp-notification__customer">${customerInfo}</p>
        <span class="rp-notification__time">
          <svg class="rp-notification__time-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          ${timeAgo}
        </span>
      </div>
      <button type="button" class="rp-notification__close" aria-label="Dismiss">
        <svg class="rp-notification__close-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
      </button>
      <div class="rp-notification__progress">
        <div class="rp-notification__progress-bar"></div>
      </div>
    `;

    return el;
  }

  function animateIn(el) {
    if (!hasGSAP() || prefersReducedMotion) {
      el.style.opacity = '1';
      el.style.transform = 'none';
      return Promise.resolve();
    }

    const isRight = config.position.includes('right');
    const isTop = config.position.includes('top');

    gsap.set(el, {
      opacity: 0,
      x: isRight ? 40 : -40,
      y: isTop ? -10 : 10,
      scale: 0.96
    });

    const tl = gsap.timeline();

    tl.to(el, {
      opacity: 1,
      x: 0,
      y: 0,
      scale: 1,
      duration: 0.4,
      ease: 'power3.out'
    });

    // Subtle settle
    tl.to(el, {
      y: isTop ? 2 : -2,
      duration: 0.1,
      ease: 'power1.out'
    }, '-=0.1');

    tl.to(el, {
      y: 0,
      duration: 0.15,
      ease: 'power1.inOut'
    });

    // Progress bar
    const progressBar = el.querySelector('.rp-notification__progress-bar');
    if (progressBar && config.duration > 0) {
      gsap.fromTo(progressBar,
        { scaleX: 1 },
        { scaleX: 0, duration: config.duration / 1000, ease: 'none', delay: 0.3 }
      );
    }

    return tl;
  }

  function animateOut(el, fast = false) {
    return new Promise((resolve) => {
      if (!hasGSAP() || prefersReducedMotion) {
        el.remove();
        resolve();
        return;
      }

      const isRight = config.position.includes('right');

      gsap.to(el, {
        opacity: 0,
        x: isRight ? 30 : -30,
        scale: 0.96,
        duration: fast ? 0.15 : 0.25,
        ease: 'power2.in',
        onComplete: () => {
          el.remove();
          resolve();
        }
      });
    });
  }

  async function fetchProducts() {
    if (productsFetched) return products;

    try {
      const response = await fetch(config.productsEndpoint);
      if (!response.ok) throw new Error('Failed to fetch');
      
      const data = await response.json();
      const excludeHandles = config.excludeProducts
        .map(h => h.trim().toLowerCase())
        .filter(h => h.length > 0);

      products = (data.products || []).filter(p => {
        const hasImage = p.images?.length > 0;
        const isExcluded = excludeHandles.includes(p.handle.toLowerCase());
        return hasImage && !isExcluded;
      });

      productsFetched = true;
      return products;
    } catch (error) {
      console.warn('RecentlyPurchased: Could not fetch products', error);
      return [];
    }
  }

  async function showNotification() {
    if (!isRunning || products.length === 0 || isDismissedForSession()) {
      if (isDismissedForSession()) stop();
      return;
    }

    if (currentNotification) {
      await animateOut(currentNotification, false);
      currentNotification = null;
    }

    const product = randomProduct(products);
    if (!product) return;

    const name = randomItem(config.names);
    const city = randomItem(config.cities);
    const customerInfo = `${name} from ${city}`;
    const minutes = randomBetween(config.timeMin, config.timeMax);
    const timeAgo = formatTimeAgo(minutes);

    const notification = createNotificationElement(product, customerInfo, timeAgo);
    currentNotification = notification;

    getContainer().appendChild(notification);
    
    // Initialize marquee after element is in DOM
    requestAnimationFrame(() => {
      const titleWrapper = notification.querySelector('.rp-notification__title-wrapper');
      initMarquee(titleWrapper);
    });
    
    animateIn(notification);

    // Click to navigate
    notification.addEventListener('click', (e) => {
      if (e.target.closest('.rp-notification__close')) return;
      const url = notification.getAttribute('data-product-url');
      if (url && url !== '#') window.location.href = url;
    });

    // Close button - dismiss for session
    const closeBtn = notification.querySelector('.rp-notification__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', async (e) => {
        e.stopPropagation();
        dismissForSession();
        stop();
        await animateOut(notification, true);
        currentNotification = null;
      });
    }

    // Auto-dismiss
    const dismissTimeout = setTimeout(async () => {
      if (currentNotification === notification) {
        await animateOut(notification, false);
        currentNotification = null;
        scheduleNext();
      }
    }, config.duration + 300);

    notification._dismissTimeout = dismissTimeout;
  }

  function scheduleNext() {
    if (!isRunning || isDismissedForSession()) {
      if (isDismissedForSession()) stop();
      return;
    }

    timeoutId = setTimeout(() => {
      requestIdle(() => {
        if (isRunning && !document.hidden) {
          showNotification();
        } else if (isRunning) {
          scheduleNext();
        }
      });
    }, config.interval);
  }

  async function start() {
    if (isRunning || isDismissedForSession()) return;
    isRunning = true;

    requestIdle(async () => {
      await fetchProducts();
      
      if (products.length === 0) {
        isRunning = false;
        return;
      }

      timeoutId = setTimeout(() => {
        if (isRunning && !document.hidden) {
          showNotification();
        } else if (isRunning) {
          scheduleNext();
        }
      }, config.initialDelay);
    });
  }

  function stop() {
    isRunning = false;
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    if (currentNotification?._dismissTimeout) {
      clearTimeout(currentNotification._dismissTimeout);
    }
  }

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && isRunning && !timeoutId && !isDismissedForSession()) {
      scheduleNext();
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    requestIdle(start);
  }

  window.RecentlyPurchased = {
    start,
    stop,
    isRunning: () => isRunning,
    getProducts: () => products,
    reset: () => {
      try { sessionStorage.removeItem(DISMISSED_KEY); } catch {}
      if (!isRunning) start();
    }
  };
})();
